import React, { useMemo } from 'react';
import {
    MdAdd,
    MdCardGiftcard,
    MdMoreVert,
    MdOutlineHistory,
    MdOutlineHome,
    MdOutlineLogin,
    MdOutlineLogout,
    MdOutlineMenu,
    MdOutlineDriveFileRenameOutline,
    MdOutlineDesignServices,
} from 'react-icons/md';
import { PiHandsClapping } from "react-icons/pi";
import { useIntl } from 'react-intl';

import { uiColors } from '../../../common/styles';
import { CompositionRegularIcon } from '../../../icons';
import {
    useAppContext,
    useAuth,
    useComposition,
    useCompositionTemplates,
    useModal,
    useNavigation,
} from '../../../context';
import { LOGIN, TEMPLATE_DRAWER, VOUCHER } from '../../../providers/NavigationProvider';
import FramiIconButton from '../FramiIconButton';
import FramiIconMenu from '../FramiIconMenu';
import {
    AdditionalMenuContentDescription,
    AdditionalMenuContentWrapper,
    SignedInAs,
    SignedInAsEmail,
    SignedInAsTopText,
} from './styled';
import { LuPackageCheck, LuUserPlus } from 'react-icons/lu';
import FramiButton from '../FramiButton';

const FramiHamburger = ({ smallDevice }) => {
    const { user, logout, galleryInfoAccepted, temporaryUser } = useAuth();
    const { isMobile } = useAppContext();
    const { openModal } = useModal();
    const { currentComp, createCompositionFromTemplate, setCurrentComp, getOrCreateVoucher } = useComposition();
    const { BASIC_TEMPLATE } = useCompositionTemplates();
    const { activeDrawer, openDrawer } = useNavigation();
    const intl = useIntl();

    const currentURL = window.location.origin;
    const urlParts = currentURL.split('.');
    const domainExtension = urlParts.length > 1 ? urlParts[urlParts.length - 1] : 'pl'; // urlParts.length === 1 is localhost:3000

    const options = useMemo(() => {
        const options = [
            {
                text: intl.formatMessage({ id: 'common.navigation.hamburger.home' }),
                icon: <MdOutlineHome />,
                onClick: () => window.open(`https://framky.${domainExtension}/`),
            },
            {
                text: intl.formatMessage({ id: 'studio.tools.hangingSystem' }),
                icon: <PiHandsClapping />,
                onClick: () => openModal('hangingSystem'),
            },
                // {
            //     text: intl.formatMessage({ id: 'common.navigation.hamburger.policy' }),
            //     icon: <MdOutlinePolicy />,
            //     onClick: () => window.open(`https://framky.${domainExtension}/privacy-policy/`),
            // },
            // {
            //     text: intl.formatMessage({ id: 'common.navigation.hamburger.terms' }),
            //     icon: <MdOutlineAssignment />,
            //     onClick: () => window.open(`https://framky.${domainExtension}/terms-and-conditions/`),
            // },
        ];

        if (!currentComp?.is_voucher) {
            options.push({
                text: intl.formatMessage({ id: 'studio.basketDrawer.addVoucher' }),
                icon: <MdCardGiftcard />,
                onClick: () => {
                    getOrCreateVoucher();
                    if (isMobile) {
                        openDrawer(VOUCHER, isMobile);
                    }
                },
            });
        } else {
            options.push({
                text: intl.formatMessage({ id: 'common.navigation.hamburger.compositions' }),
                icon: <CompositionRegularIcon style={{ width: '0.6em' }} />,
                onClick: async () => {
                    const composition = await createCompositionFromTemplate(BASIC_TEMPLATE);
                    setCurrentComp(composition);
                    openDrawer(TEMPLATE_DRAWER);
                },
            });
        }

        if (!user && !galleryInfoAccepted) {
            options.push({
                name: 'login',
                text: intl.formatMessage({ id: 'common.navigation.hamburger.login' }),
                icon: <MdOutlineLogin />,
                onClick: () => openModal('login'),
            });
        } else if (!user && galleryInfoAccepted) {
            options.push({
                name: 'logout',
                text: intl.formatMessage({ id: 'common.navigation.hamburger.logout' }),
                icon: <MdOutlineLogout />,
                onClick: logout,
            })
        } else if (user) {
            let framisHistory = [
                {
                    text: intl.formatMessage({ id: 'studio.basketDrawer.addItem' }),
                    icon: <MdAdd fill={uiColors.PRIMARY_500} />,
                    onClick: () => openModal('newGallery'),

                },
                {
                    text: intl.formatMessage({ id: 'common.navigation.hamburger.rename' }),
                    icon: <MdOutlineDriveFileRenameOutline fill={uiColors.PRIMARY_500}/>,
                    onClick: () => openModal('changeName'),
                    divider: true,
                },
            ];

            framisHistory = framisHistory.concat(
                user.frami_set.map((frami, i) => {
                    const date = new Date(frami.created_at);
                    let text = '';
                    let url = `${currentURL}/${frami?.id}`;
                    text += `${padZero(date.getDate())}/${padZero(date.getMonth() + 1)}/${date.getFullYear()}`;
                    if (frami.friendly_name !== 'Name') {
                        text += ` ${frami.friendly_name}`;
                    } else {
                        text += ` ${padZero(date.getHours())}:${padZero(date.getMinutes())}`;
                    }

                    return {
                        text: text,
                        icon: frami.is_ordered ? <LuPackageCheck /> : <MdOutlineDesignServices />,
                        onClick: () => (window.location.href = url),
                    };
                })
            );

            options.push(
                {
                    text: intl.formatMessage({ id: 'common.navigation.hamburger.history' }),
                    icon: <MdOutlineHistory />,
                    subMenu: framisHistory,
                },
                {
                    name: 'logout',
                    text: intl.formatMessage({ id: 'common.navigation.hamburger.logout' }),
                    icon: <MdOutlineLogout />,
                    onClick: logout,
                }
            );
        }

        return options;
    }, [user, currentComp?.is_voucher, activeDrawer]);

    return (
        <>
            <FramiIconMenu
                options={options}
                topComponent={(handleClose) => (
                    (user || (galleryInfoAccepted && temporaryUser)) && (
                        <SignedInAs>
                            {user && (
                                <SignedInAsTopText>
                                    {intl.formatMessage({ id: 'common.navigation.hamburger.signed' })}
                                </SignedInAsTopText>
                            )}
                            <SignedInAsEmail data-cy={'signed-as-text-row'}>
                                {user ? user.email : temporaryUser.email}
                            </SignedInAsEmail>
                            {!user && (
                                <AdditionalMenuContentWrapper>
                                    <AdditionalMenuContentDescription>
                                        {intl.formatMessage({ id: 'common.navigation.hamburger.fullAccess' })}
                                    </AdditionalMenuContentDescription>
                                    <FramiButton
                                        className={'additional-menu-button'}
                                        dataPh={'create full account'}
                                        dataCy={'create-full-account-button'}
                                        variant={'secondary'}
                                        onClick={() => {
                                            openModal('login', { registerMode: true });
                                            handleClose();
                                        }}
                                        leftIcon={<LuUserPlus size={20}/>}
                                        title={intl.formatMessage({
                                            id: 'common.navigation.hamburger.createFullAccount'
                                        })}
                                    />
                                </AdditionalMenuContentWrapper>
                            )}
                        </SignedInAs>
                    )
                )}
            >
                {(openMenu) => (
                    <FramiIconButton
                        className={`navbutton ${smallDevice ? ' right' : ' left'}`}
                        title={'profile menu'}
                        dataCy={'login-menu-button'}
                        size={50}
                        onClick={openMenu}
                        icon={smallDevice ? <MdMoreVert /> : <MdOutlineMenu />}
                    />
                )}
            </FramiIconMenu>
        </>
    );
};

const padZero = (value) => {
    return value.toString().padStart(2, '0');
};

export default FramiHamburger;
